import React, { useState, useEffect, useRef } from 'react';

function CRUDList({ title, api, columns, FormComponent, formProps = {}, renderRow, defaultFormData, sortFn, filterFn, filterComponent, queryParams, isFormValid, getRowStyle, disableDelete, disableCreate, disableEdit, extraActions, useOldStyle, renderActions, renderFormActions, hideActionsHeader = false, tableClassName = '' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultFormData || {});
  const [error, setError] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  const queryKey = JSON.stringify(queryParams || {});

  useEffect(() => {
    loadItems();
  }, [queryKey]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close menu if click is outside any dropdown menu
      if (!e.target.closest('.crud-menu') && !e.target.closest('.crud-menu-button')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await api.getAll(queryParams);
      // Handle both old array format and new object with listData
      const data = Array.isArray(response.data) ? response.data : (response.data.listData || []);
      setItems(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData(defaultFormData || {});
    setEditingId(null);
    setShowForm(true);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const handleEdit = (item) => {
    // Extract only the fields that exist in defaultFormData
    const editData = {};
    Object.keys(defaultFormData || {}).forEach(key => {
      let value = item[key] !== undefined ? item[key] : defaultFormData[key];

      // Format dates for input type="date" (YYYY-MM-DD)
      if ((key.includes('_date') || key.endsWith('_at')) && value && typeof value === 'string' && value.includes('T')) {
        value = value.split('T')[0];
      }

      // Format time for input type="time" (HH:MM)
      if (key.includes('_time') && value && typeof value === 'string' && value.length > 5) {
        value = value.substring(0, 5);
      }

      editData[key] = value;
    });
    setFormData(editData);
    setEditingId(item[Object.keys(item)[0]]);
    setShowForm(true);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await api.delete(id);
      setShowForm(false);
      setEditingId(null);
      setFormData(defaultFormData || {});
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await api.update(editingId, formData);
      } else {
        await api.create(formData);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData(defaultFormData || {});
    setEditingId(null);
    setError('');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (showForm) {
    return (
      <div>
        <div className="page-header">
          <h2>{editingId ? 'Edit' : 'Create'} {title}</h2>
        </div>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit} className="form-container">
          {FormComponent ? (
            <FormComponent {...formProps} formData={formData} setFormData={setFormData} editingId={editingId} onDelete={handleDelete} />
          ) : null}

          <div className="form-actions">
            <button type="submit" className="btn btn-success" disabled={isFormValid === false}>Save</button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
            {renderFormActions ? renderFormActions({ editingId, onDelete: handleDelete }) : null}
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'space-between', width: '100%' }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {filterComponent}
            {extraActions}
            {!disableCreate && <button className="btn btn-primary" onClick={handleCreate}>Add New</button>}
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {(() => {
        const filteredItems = items.filter(filterFn || (() => true));
        const sortedItems = sortFn ? [...filteredItems].sort(sortFn) : filteredItems;
        return sortedItems.length === 0 ? (
          <div className="empty-state">
            <h3>No items found</h3>
            <p>Click "Add New" to create your first {title.toLowerCase()}</p>
          </div>
        ) : (
          <div className="table-container">
            <table className={tableClassName}>
              <thead>
                <tr>
                  {columns.map((col, idx) => (
                    <th key={idx} style={useOldStyle ? {} : { textAlign: 'center' }}>{col}</th>
                  ))}
                  {!disableEdit && <th aria-label="Actions" style={useOldStyle ? {} : { textAlign: 'center' }}>{hideActionsHeader ? null : 'Actions'}</th>}
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item) => {
                  const deleteDisabled = typeof disableDelete === 'function' ? disableDelete(item) : false;
                  const itemId = item[Object.keys(item)[0]];
                  const isMenuOpen = openMenuId === itemId;
                  
                  return (
                    <tr key={itemId} style={getRowStyle ? getRowStyle(item) : {}}>
                      {renderRow(item)}
                      {!disableEdit && (
                        renderActions ? (
                          <td>{renderActions({ item, itemId, onEdit: handleEdit, onDelete: handleDelete, deleteDisabled })}</td>
                        ) : useOldStyle ? (
                          <td>
                            <div className="actions">
                              <button className="btn btn-primary" onClick={() => handleEdit(item)}>Edit</button>
                              <button 
                                className="btn btn-danger" 
                                onClick={() => handleDelete(itemId)}
                                disabled={deleteDisabled}
                                title={deleteDisabled ? 'Cannot delete this item' : ''}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        ) : (
                          <td style={{ textAlign: 'center', backgroundColor: getRowStyle ? getRowStyle(item).backgroundColor : 'transparent', position: 'relative' }} ref={menuRef}>
                            <button 
                            className="btn btn-secondary crud-menu-button"
                            onClick={() => setOpenMenuId(isMenuOpen ? null : itemId)}
                            style={{ padding: '6px 12px' }}
                          >
                            ⋮
                          </button>
                          {isMenuOpen && (
                            <div 
                              className="crud-menu"
                              style={{
                                position: 'absolute',
                                right: '10px',
                                top: '100%',
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                zIndex: 1000,
                                minWidth: '120px',
                                marginTop: '2px'
                              }}
                            >
                              <button
                                className="btn"
                                onClick={() => {
                                  handleEdit(item);
                                  setOpenMenuId(null);
                                }}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: 'transparent',
                                  cursor: 'pointer',
                                  borderRadius: '4px 4px 0 0'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                Edit
                              </button>
                              <button
                                className="btn"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  handleDelete(itemId);
                                }}
                                disabled={deleteDisabled}
                                title={deleteDisabled ? 'Cannot delete this item' : ''}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: 'transparent',
                                  cursor: deleteDisabled ? 'not-allowed' : 'pointer',
                                  color: deleteDisabled ? '#999' : '#c0392b',
                                  borderRadius: '0 0 4px 4px'
                                }}
                                onMouseEnter={(e) => !deleteDisabled && (e.target.style.backgroundColor = '#f5f5f5')}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                          </td>
                        )
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}

export default CRUDList;
